import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus } from '@fortawesome/free-solid-svg-icons';
import { apiJson } from '../../components/apiClient';

// Busca global + paleta de comandos (§3/§4/§12 do diagnóstico de
// usabilidade — melhorias de automação). Abre com Cmd/Ctrl+K de qualquer
// tela, ou pelo botão de busca na topbar. Sem termo digitado, mostra os
// mesmos atalhos de criação do QuickCreate (reaproveitado, não duplicado);
// com termo, busca Clientes/Projetos/Documentos em paralelo e agrupa por
// tipo — cada resultado já navega direto (mesma ponte contextual usada em
// todo o resto do app). Em resultados de Cliente, um botão "+ Receita"
// executa a ação sem passar pela navegação normal (começo da "paleta que
// executa ações", item 12 do diagnóstico).
function CommandPalette({ open, onClose, onNavegar, modulosHabilitados, permissaoClientes, crmAcessivel }) {
  const [query, setQuery] = useState('');
  const [clientes, setClientes] = useState(null);
  const [projetos, setProjetos] = useState(null);
  const [documentos, setDocumentos] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setClientes(null);
      setProjetos(null);
      setDocumentos(null);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    const termo = query.trim();
    if (!termo) { setClientes(null); setProjetos(null); setDocumentos(null); return; }
    setBuscando(true);
    debounceRef.current = setTimeout(async () => {
      const buscas = [];
      if (crmAcessivel && permissaoClientes) buscas.push(apiJson(`/clientes?nome=${encodeURIComponent(termo)}`).then((r) => setClientes(r.status === 'ok' ? r.lista.slice(0, 6) : [])));
      else setClientes([]);
      if (modulosHabilitados?.projetos) {
        buscas.push(apiJson('/projetos').then((r) => {
          const termoLower = termo.toLowerCase();
          setProjetos(r.status === 'ok' ? r.lista.filter((p) => p.nome.toLowerCase().includes(termoLower)).slice(0, 6) : []);
        }));
      } else setProjetos([]);
      if (crmAcessivel) buscas.push(apiJson(`/documentos?busca=${encodeURIComponent(termo)}`).then((r) => setDocumentos(r.status === 'ok' ? r.lista.slice(0, 6) : [])));
      else setDocumentos([]);
      await Promise.all(buscas);
      setBuscando(false);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, crmAcessivel, permissaoClientes, modulosHabilitados]);

  if (!open) return null;

  const acoesRapidas = [
    { label: 'Novo Cliente', destino: 'clientes', visivel: crmAcessivel && permissaoClientes },
    { label: 'Novo Projeto', destino: 'projetos', visivel: modulosHabilitados?.projetos },
    { label: 'Nova Receita', destino: 'financeiro', contexto: { abrirCriacaoFinanceiro: 'RECEITA' }, visivel: modulosHabilitados?.financeiro },
    { label: 'Nova Assinatura', destino: 'financeiro', contexto: { abrirCriacaoFinanceiro: 'ASSINATURA' }, visivel: modulosHabilitados?.financeiro },
    { label: 'Novo Produto', destino: 'estoque', visivel: modulosHabilitados?.estoque },
    { label: 'Novo Ativo', destino: 'patrimonio', visivel: modulosHabilitados?.patrimonio },
    { label: 'Novo Documento', destino: 'documentos', visivel: crmAcessivel },
  ].filter((i) => i.visivel);

  const irPara = (destino, contexto) => { onNavegar(destino, contexto); onClose(); };
  const criarReceitaParaCliente = (clienteId) => { onNavegar('financeiro', { clienteIdFinanceiro: clienteId, abrirCriacaoFinanceiro: 'RECEITA' }); onClose(); };

  const semResultados = query.trim() && !buscando && (clientes?.length || 0) === 0 && (projetos?.length || 0) === 0 && (documentos?.length || 0) === 0;

  return (
    <div className="crm-overlay crm-dialog-overlay" style={{ alignItems: 'flex-start', paddingTop: '10vh' }} onClick={onClose}>
      <div className="crm-dialog" style={{ maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', padding: 'var(--crm-space-4) var(--crm-space-4) var(--crm-space-3)', borderBottom: '1px solid var(--crm-border)' }}>
          <FontAwesomeIcon icon={faSearch} style={{ color: 'var(--crm-text-tertiary)' }} />
          <input
            ref={inputRef}
            className="crm-input"
            style={{ border: 'none', boxShadow: 'none', fontSize: 'var(--crm-text-base)', padding: 0 }}
            placeholder="Buscar cliente, projeto... ou escolha uma ação rápida"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="crm-form-hint mono" style={{ fontSize: '11px' }}>Esc</span>
        </div>

        <div style={{ maxHeight: '55vh', overflowY: 'auto', padding: 'var(--crm-space-2)' }}>
          {!query.trim() && (
            <>
              <div className="crm-dropdown-section-label">Ações rápidas</div>
              {acoesRapidas.map((a) => (
                <button key={a.label} type="button" className="crm-dropdown-item" onClick={() => irPara(a.destino, a.contexto)}>
                  <FontAwesomeIcon icon={faPlus} /> {a.label}
                </button>
              ))}
            </>
          )}

          {query.trim() && buscando && (
            <div className="crm-form-hint" style={{ padding: 'var(--crm-space-3)' }}>Buscando...</div>
          )}

          {query.trim() && !buscando && clientes?.length > 0 && (
            <>
              <div className="crm-dropdown-section-label">Clientes</div>
              {clientes.map((c) => (
                <div key={c._id} className="crm-dropdown-item" style={{ justifyContent: 'space-between', cursor: 'default' }}>
                  <button type="button" style={{ all: 'unset', cursor: 'pointer', flex: 1 }} onClick={() => irPara('clientes', { clienteId: c._id })}>{c.nome}</button>
                  <button type="button" className="crm-icon-btn" title="Nova receita para este cliente" onClick={() => criarReceitaParaCliente(c._id)}>
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              ))}
            </>
          )}

          {query.trim() && !buscando && projetos?.length > 0 && (
            <>
              <div className="crm-dropdown-section-label">Projetos</div>
              {projetos.map((p) => (
                <button key={p.id} type="button" className="crm-dropdown-item" onClick={() => irPara('projetos', { projetoId: p.id })}>
                  {p.nome}
                </button>
              ))}
            </>
          )}

          {query.trim() && !buscando && documentos?.length > 0 && (
            <>
              <div className="crm-dropdown-section-label">Documentos</div>
              {documentos.map((d) => (
                <button key={d.id} type="button" className="crm-dropdown-item" onClick={() => irPara('documentos', { documentoId: d.id })}>
                  {d.titulo}
                </button>
              ))}
            </>
          )}

          {semResultados && (
            <div className="crm-form-hint" style={{ padding: 'var(--crm-space-3)' }}>Nenhum resultado para "{query}"</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
